import * as Schema from "effect/Schema";
import { ContentHash, Dimensions, Identifier, NonEmptyText, NonNegative, SafeInteger, freezeMetadata, hasUniqueIds } from "@linempo/kernel";
import { MaskDescriptor, PathDescriptor, RasterDescriptor } from "./plan.js";
export const AnimationInspection = Schema.Struct({
    animationId: Identifier,
    assetId: Identifier,
    /** Definition fingerprint, independent of revision, placement and timing. */
    inputKey: NonEmptyText,
    /** Includes generator compatibility/settings and source content. */
    cacheKey: ContentHash,
    status: Schema.Literals(["ready", "missing", "invalid"]),
    byteLength: SafeInteger,
    pathCount: SafeInteger,
    error: Schema.optionalKey(Schema.String),
});
export const AnimationGenerationResult = Schema.Struct({
    animationId: Identifier,
    assetId: Identifier,
    inputKey: NonEmptyText,
    cacheKey: ContentHash,
    byteLength: SafeInteger,
    pathCount: SafeInteger,
    elapsedMs: NonNegative,
    reused: Schema.Boolean,
});
/** Source-space geometry; no timeline times, placement, captions or native handles. */
export const AnimationGeometryMetadata = Schema.Struct({
    schemaVersion: Schema.Literal(1),
    cacheKey: ContentHash,
    inputKey: NonEmptyText,
    assetHash: ContentHash,
    dimensions: Dimensions,
    algorithmVersion: NonEmptyText,
    paths: Schema.Array(PathDescriptor),
    rasters: Schema.Array(RasterDescriptor),
    masks: Schema.Array(MaskDescriptor),
    automaticPathIds: Schema.Array(Identifier),
    automaticRasterId: Schema.NullOr(Identifier),
    sourceRasterId: Identifier,
    /** One mask per fill in definition order; identifiers of editable fills are not cached. */
    fillMaskIds: Schema.Array(Identifier),
});
export const animationGeometryBytes = (geometry) => [...geometry.pathBuffers.values(), ...geometry.rasterBuffers.values(), ...geometry.maskBuffers.values()]
    .reduce((total, buffer) => total + buffer.byteLength, 0);
/** Validate stored/generated binary ownership before it can enter plan assembly. */
export function validateAnimationGeometry(input) {
    const metadata = freezeMetadata(Schema.decodeUnknownSync(AnimationGeometryMetadata)(input.metadata));
    for (const descriptors of [metadata.paths, metadata.rasters, metadata.masks]) {
        if (!hasUniqueIds(descriptors))
            throw new TypeError("Duplicate animation descriptor ID");
    }
    const paths = new Set(metadata.paths.map((path) => path.id));
    const rasters = new Set(metadata.rasters.map((raster) => raster.id));
    const masks = new Set(metadata.masks.map((mask) => mask.id));
    if (metadata.automaticPathIds.some((id) => !paths.has(id)) || new Set(metadata.automaticPathIds).size !== metadata.automaticPathIds.length
        || !rasters.has(metadata.sourceRasterId) || metadata.automaticRasterId !== null && !rasters.has(metadata.automaticRasterId)
        || metadata.fillMaskIds.some((id) => !masks.has(id)))
        throw new TypeError("Unknown animation geometry reference");
    const referenced = { path: new Set(), raster: new Set(), mask: new Set() };
    for (const path of metadata.paths) {
        const buffer = input.pathBuffers.get(path.bufferId);
        if (!(buffer instanceof Float64Array) || buffer.length % 3 || (path.vertexOffset + path.vertexCount) * 3 > buffer.length)
            throw new TypeError("Invalid animation path buffer");
        referenced.path.add(path.bufferId);
        let previous = -1;
        for (let vertex = 0; vertex < path.vertexCount; vertex++) {
            const offset = (path.vertexOffset + vertex) * 3;
            const x = buffer[offset];
            const y = buffer[offset + 1];
            const length = buffer[offset + 2];
            if (![x, y, length].every(Number.isFinite) || x < 0 || y < 0 || x > metadata.dimensions.width || y > metadata.dimensions.height
                || length < 0 || length < previous || vertex === 0 && length !== 0)
                throw new TypeError("Invalid animation path coordinates or length");
            previous = length;
        }
        if (Math.abs(previous - path.length) > 1e-6)
            throw new TypeError("Animation path length mismatch");
    }
    for (const raster of metadata.rasters) {
        const buffer = input.rasterBuffers.get(raster.bufferId);
        if (!(buffer instanceof Uint8Array) || buffer.byteLength !== raster.dimensions.width * raster.dimensions.height * 4
            || raster.assetHash !== metadata.assetHash || raster.dimensions.width !== metadata.dimensions.width || raster.dimensions.height !== metadata.dimensions.height)
            throw new TypeError("Invalid animation raster buffer");
        referenced.raster.add(raster.bufferId);
    }
    for (const mask of metadata.masks) {
        const buffer = input.maskBuffers.get(mask.bufferId);
        if (!(buffer instanceof Uint8Array) || buffer.byteLength !== mask.dimensions.width * mask.dimensions.height)
            throw new TypeError("Invalid animation mask buffer");
        referenced.mask.add(mask.bufferId);
    }
    if (referenced.path.size !== input.pathBuffers.size || referenced.raster.size !== input.rasterBuffers.size || referenced.mask.size !== input.maskBuffers.size)
        throw new TypeError("Unreferenced animation buffer");
    return { metadata, pathBuffers: input.pathBuffers, rasterBuffers: input.rasterBuffers, maskBuffers: input.maskBuffers };
}
